import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DollarSign, FileText, CreditCard, TrendingUp, Plus,
  Eye, Download, Filter, Search, CheckCircle2, Clock, XCircle
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import { format } from 'date-fns';

const FinanceManagement = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [showAddInvoice, setShowAddInvoice] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);

  const [newInvoice, setNewInvoice] = useState({
    client_id: '',
    amount: '',
    tax: '15',
    due_date: '',
    description: ''
  });

  const [newExpense, setNewExpense] = useState({
    category: '',
    amount: '',
    description: ''
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error(i18n.language === 'ar' ? 'غير مصرح' : 'Unauthorized');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invRes, payRes, expRes] = await Promise.all([
        api.get('/finance/invoices'),
        api.get('/finance/payments'),
        api.get('/finance/expenses')
      ]);
      setInvoices(invRes.data);
      setPayments(payRes.data);
      setExpenses(expRes.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddInvoice = async (e) => {
    e.preventDefault();
    try {
      await api.post('/finance/invoices', newInvoice);
      toast.success(i18n.language === 'ar' ? 'تم إضافة الفاتورة بنجاح' : 'Invoice added successfully');
      setShowAddInvoice(false);
      setNewInvoice({ client_id: '', amount: '', tax: '15', due_date: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إضافة الفاتورة' : 'Failed to add invoice');
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await api.post('/finance/expenses', newExpense);
      toast.success(i18n.language === 'ar' ? 'تم إضافة المصروف بنجاح' : 'Expense added successfully');
      setShowAddExpense(false);
      setNewExpense({ category: '', amount: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إضافة المصروف' : 'Failed to add expense');
    }
  };

  const totalInvoices = invoices.reduce((sum, inv) => sum + inv.total_amount, 0);
  const paidInvoices = invoices.filter(inv => inv.status === 'paid');
  const totalPaid = paidInvoices.reduce((sum, inv) => sum + inv.total_amount, 0);
  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);

  const stats = [
    {
      title: i18n.language === 'ar' ? 'إجمالي الفواتير' : 'Total Invoices',
      value: `${totalInvoices.toFixed(2)} ${i18n.language === 'ar' ? 'ريال' : 'SAR'}`,
      icon: FileText,
      color: 'from-blue-500 to-blue-600',
      count: invoices.length
    },
    {
      title: i18n.language === 'ar' ? 'المدفوعات' : 'Payments',
      value: `${totalPaid.toFixed(2)} ${i18n.language === 'ar' ? 'ريال' : 'SAR'}`,
      icon: CheckCircle2,
      color: 'from-green-500 to-green-600',
      count: paidInvoices.length
    },
    {
      title: i18n.language === 'ar' ? 'المصروفات' : 'Expenses',
      value: `${totalExpenses.toFixed(2)} ${i18n.language === 'ar' ? 'ريال' : 'SAR'}`,
      icon: TrendingUp,
      color: 'from-red-500 to-red-600',
      count: expenses.length
    },
    {
      title: i18n.language === 'ar' ? 'الأرباح' : 'Profit',
      value: `${(totalPaid - totalExpenses).toFixed(2)} ${i18n.language === 'ar' ? 'ريال' : 'SAR'}`,
      icon: DollarSign,
      color: 'from-yellow-500 to-yellow-600'
    }
  ];

  const getStatusBadge = (status) => {
    const variants = {
      paid: 'default',
      pending: 'secondary',
      overdue: 'destructive'
    };
    const labels = {
      paid: i18n.language === 'ar' ? 'مدفوع' : 'Paid',
      pending: i18n.language === 'ar' ? 'معلق' : 'Pending',
      overdue: i18n.language === 'ar' ? 'متأخر' : 'Overdue'
    };
    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                {i18n.language === 'ar' ? 'النظام المالي' : 'Finance Management'}
              </h1>
              <p className="text-text-secondary text-lg">
                {i18n.language === 'ar' ? 'إدارة الفواتير والمدفوعات والمصروفات' : 'Manage invoices, payments and expenses'}
              </p>
            </div>
            <div className="flex gap-2">
              <Dialog open={showAddInvoice} onOpenChange={setShowAddInvoice}>
                <DialogTrigger asChild>
                  <Button className="btn-primary gap-2">
                    <Plus className="w-5 h-5" />
                    {i18n.language === 'ar' ? 'فاتورة جديدة' : 'New Invoice'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>{i18n.language === 'ar' ? 'إنشاء فاتورة جديدة' : 'Create New Invoice'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddInvoice} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>{i18n.language === 'ar' ? 'معرف العميل' : 'Client ID'}</Label>
                        <Input value={newInvoice.client_id} onChange={(e) => setNewInvoice({...newInvoice, client_id: e.target.value})} required />
                      </div>
                      <div>
                        <Label>{i18n.language === 'ar' ? 'المبلغ' : 'Amount'}</Label>
                        <Input type="number" step="0.01" value={newInvoice.amount} onChange={(e) => setNewInvoice({...newInvoice, amount: e.target.value})} required />
                      </div>
                      <div>
                        <Label>{i18n.language === 'ar' ? 'الضريبة (%)' : 'Tax (%)'}</Label>
                        <Input type="number" step="0.01" value={newInvoice.tax} onChange={(e) => setNewInvoice({...newInvoice, tax: e.target.value})} required />
                      </div>
                      <div>
                        <Label>{i18n.language === 'ar' ? 'تاريخ الاستحقاق' : 'Due Date'}</Label>
                        <Input type="date" value={newInvoice.due_date} onChange={(e) => setNewInvoice({...newInvoice, due_date: e.target.value})} required />
                      </div>
                      <div className="col-span-2">
                        <Label>{i18n.language === 'ar' ? 'الوصف' : 'Description'}</Label>
                        <Input value={newInvoice.description} onChange={(e) => setNewInvoice({...newInvoice, description: e.target.value})} />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowAddInvoice(false)}>
                        {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                      </Button>
                      <Button type="submit" className="btn-primary">
                        {i18n.language === 'ar' ? 'إنشاء' : 'Create'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <Plus className="w-5 h-5" />
                    {i18n.language === 'ar' ? 'مصروف' : 'Expense'}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{i18n.language === 'ar' ? 'إضافة مصروف' : 'Add Expense'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddExpense} className="space-y-4">
                    <div>
                      <Label>{i18n.language === 'ar' ? 'الفئة' : 'Category'}</Label>
                      <Input value={newExpense.category} onChange={(e) => setNewExpense({...newExpense, category: e.target.value})} required />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'المبلغ' : 'Amount'}</Label>
                      <Input type="number" step="0.01" value={newExpense.amount} onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})} required />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'الوصف' : 'Description'}</Label>
                      <Input value={newExpense.description} onChange={(e) => setNewExpense({...newExpense, description: e.target.value})} required />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowAddExpense(false)}>
                        {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                      </Button>
                      <Button type="submit" className="btn-primary">
                        {i18n.language === 'ar' ? 'إضافة' : 'Add'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="relative overflow-hidden border-0 shadow-lg">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                <CardContent className="pt-6 relative">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-sm text-text-secondary mb-2">{stat.title}</p>
                      <p className="text-2xl font-bold">{stat.value}</p>
                      {stat.count !== undefined && (
                        <p className="text-sm text-text-secondary mt-1">{stat.count} {i18n.language === 'ar' ? 'عنصر' : 'items'}</p>
                      )}
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Tabs defaultValue="invoices" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="invoices">{i18n.language === 'ar' ? 'الفواتير' : 'Invoices'}</TabsTrigger>
              <TabsTrigger value="payments">{i18n.language === 'ar' ? 'المدفوعات' : 'Payments'}</TabsTrigger>
              <TabsTrigger value="expenses">{i18n.language === 'ar' ? 'المصروفات' : 'Expenses'}</TabsTrigger>
            </TabsList>

            <TabsContent value="invoices">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'الفواتير' : 'Invoices'}</CardTitle>
                </CardHeader>
                <CardContent>
                  {invoices.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-text-secondary mx-auto mb-3" />
                      <p className="text-text-secondary">{i18n.language === 'ar' ? 'لا توجد فواتير' : 'No invoices'}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {invoices.map((invoice) => (
                        <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <p className="font-semibold">{invoice.invoice_number}</p>
                              {getStatusBadge(invoice.status)}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-text-secondary">
                              <span>{i18n.language === 'ar' ? 'المبلغ:' : 'Amount:'} {invoice.total_amount} {i18n.language === 'ar' ? 'ريال' : 'SAR'}</span>
                              <span>{i18n.language === 'ar' ? 'الاستحقاق:' : 'Due:'} {format(new Date(invoice.due_date), 'dd/MM/yyyy')}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline"><Eye className="w-4 h-4" /></Button>
                            <Button size="sm" variant="outline"><Download className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="payments">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'المدفوعات' : 'Payments'}</CardTitle>
                </CardHeader>
                <CardContent>
                  {payments.length === 0 ? (
                    <div className="text-center py-12">
                      <CreditCard className="w-12 h-12 text-text-secondary mx-auto mb-3" />
                      <p className="text-text-secondary">{i18n.language === 'ar' ? 'لا توجد مدفوعات' : 'No payments'}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {payments.map((payment) => (
                        <div key={payment.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <p className="font-semibold mb-1">{payment.amount} {i18n.language === 'ar' ? 'ريال' : 'SAR'}</p>
                            <p className="text-sm text-text-secondary">
                              {payment.payment_method} - {format(new Date(payment.payment_date), 'dd/MM/yyyy HH:mm')}
                            </p>
                          </div>
                          <Badge>{payment.status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="expenses">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'المصروفات' : 'Expenses'}</CardTitle>
                </CardHeader>
                <CardContent>
                  {expenses.length === 0 ? (
                    <div className="text-center py-12">
                      <TrendingUp className="w-12 h-12 text-text-secondary mx-auto mb-3" />
                      <p className="text-text-secondary">{i18n.language === 'ar' ? 'لا توجد مصروفات' : 'No expenses'}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {expenses.map((expense) => (
                        <div key={expense.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <p className="font-semibold mb-1">{expense.category}</p>
                            <p className="text-sm text-text-secondary">{expense.description}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg text-red-600">{expense.amount} {i18n.language === 'ar' ? 'ريال' : 'SAR'}</p>
                            <p className="text-xs text-text-secondary">{format(new Date(expense.date), 'dd/MM/yyyy')}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FinanceManagement;